/**
 * Lens Advisor Prescription Configurator Controller (Dutch, FormData Image Upload & Cart Display Ordering)
 */
class LensAdvisor {
  constructor() {
    this.modal = document.getElementById('la-modal-overlay');
    if (!this.modal) return;

    this.historyStack = ['step-1'];
    this.currentStepId = 'step-1';

    // State object to store all user selections
    this.state = {
      prescriptionType: '',
      glassOption: '',
      glassPrice: 0,
      lensOption: '',
      lensPrice: 0,
      matchingColor: 'Gray',
      sunglassesType: 'Tinted',
      sunglassesColor: 'Gray',
      recipeMethod: '',
      recipeFile: null,
      pdType: 'Totale PD',
      totalPd: 'Selecteer',
      pdRight: 'Selecteer',
      pdLinks: 'Selecteer',
      manualOD: { sph: '0.00', cyl: '0.00', ash: 'Selecteer', add: 'Selecteer' },
      manualOS: { sph: '0.00', cyl: '0.00', ash: 'Selecteer', add: 'Selecteer' },
      segmentHeight: 'Selecteer',
      orderNotes: ''
    };

    this.initEvents();
  }

  initEvents() {
    // Open Modal Triggers
    document.querySelectorAll('.la-open-modal-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.openModal();
      });
    });

    // Close Modal
    const closeBtn = document.getElementById('la-btn-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.closeModal());
    }

    // Back Button
    const backBtn = document.getElementById('la-btn-back');
    if (backBtn) {
      backBtn.addEventListener('click', () => this.goBack());
    }

    // Conditional ASH dependency logic (SPH or CYL != 0.00 enables ASH)
    const odSph = document.getElementById('la-od-sph');
    const odCyl = document.getElementById('la-od-cyl');
    const odAsh = document.getElementById('la-od-ash');

    const osSph = document.getElementById('la-os-sph');
    const osCyl = document.getElementById('la-os-cyl');
    const osAsh = document.getElementById('la-os-ash');

    const updateAshState = (sphEl, cylEl, ashEl) => {
      if (!sphEl || !cylEl || !ashEl) return;
      const sphVal = sphEl.value;
      const cylVal = cylEl.value;
      const hasSphValue = sphVal && sphVal !== '0.00' && sphVal !== 'To elect';
      const hasCylValue = cylVal && cylVal !== '0.00' && cylVal !== 'To elect';

      if (hasSphValue || hasCylValue) {
        ashEl.disabled = false;
      } else {
        ashEl.disabled = true;
        ashEl.value = 'To elect';
      }
    };

    if (odSph) odSph.addEventListener('change', () => updateAshState(odSph, odCyl, odAsh));
    if (odCyl) odCyl.addEventListener('change', () => updateAshState(odSph, odCyl, odAsh));
    if (osSph) osSph.addEventListener('change', () => updateAshState(osSph, osCyl, osAsh));
    if (osCyl) osCyl.addEventListener('change', () => updateAshState(osSph, osCyl, osAsh));

    // Step 1 Options
    document.querySelectorAll('[data-la-step1]').forEach(card => {
      card.addEventListener('click', () => {
        this.state.prescriptionType = card.getAttribute('data-la-step1');
        this.goToStep('step-2');
      });
    });

    // Step 2 Options (Helder, Meekleuren, Zonnebril)
    document.querySelectorAll('[data-la-step2]').forEach(card => {
      card.addEventListener('click', () => {
        const option = card.getAttribute('data-la-step2');
        const price = parseFloat(card.getAttribute('data-la-glass-price') || 0);

        this.state.glassOption = option;
        this.state.glassPrice = price;

        if (option === 'Helder' || option === 'Bright') {
          this.goToStep('step-3-distant');
        } else if (option === 'Meekleuren' || option === 'Matching') {
          this.goToStep('step-3-matching');
        } else if (option === 'Zonnebril' || option === 'Sunglasses') {
          this.goToStep('step-3-sunglasses');
        } else {
          this.goToStep('step-3-distant');
        }
      });
    });

    // Step 3A: Distant Lens Options (Inbegrepen, Dun, Dunner, Dunst)
    document.querySelectorAll('[data-la-lens]').forEach(card => {
      card.addEventListener('click', () => {
        this.state.lensOption = card.getAttribute('data-la-lens');
        this.state.lensPrice = parseFloat(card.getAttribute('data-la-price') || 0);
        this.goToStep('step-4-recipe');
      });
    });

    // Helper function for smooth image preview swapping
    const swapPreviewImage = (imgEl, newUrl) => {
      if (!imgEl || !newUrl) return;
      if (imgEl.src === newUrl) return;

      imgEl.classList.add('is-fading');
      setTimeout(() => {
        imgEl.src = newUrl;
        imgEl.classList.remove('is-fading');
      }, 200);
    };

    // Step 3B: Meekleuren Color Swatches & Dynamic Preview Image (Up to 40 Colors)
    document.querySelectorAll('.la-color-swatch-card[data-la-color-group="matching"]').forEach(card => {
      card.addEventListener('click', () => {
        const radio = card.querySelector('input[type="radio"]');
        if (radio) radio.checked = true;

        document.querySelectorAll('.la-color-swatch-card[data-la-color-group="matching"]').forEach(c => {
          c.classList.remove('is-selected');
          const badge = c.querySelector('.la-color-check-badge');
          if (badge) badge.style.display = 'none';
        });

        card.classList.add('is-selected');
        const activeBadge = card.querySelector('.la-color-check-badge');
        if (activeBadge) activeBadge.style.display = 'inline';

        const colorVal = card.querySelector('input')?.value || card.querySelector('.la-color-title')?.textContent?.trim() || 'Gray';
        this.state.matchingColor = colorVal;

        const imgUrl = card.getAttribute('data-img-url');
        const previewImg = document.getElementById('la-matching-preview-img');
        if (previewImg && imgUrl) {
          swapPreviewImage(previewImg, imgUrl);
        }
      });
    });

    const btnSaveMatching = document.getElementById('la-save-matching');
    if (btnSaveMatching) {
      btnSaveMatching.addEventListener('click', () => {
        const selectedRadio = document.querySelector('input[name="la-matching-color"]:checked');
        if (selectedRadio) this.state.matchingColor = selectedRadio.value;
        this.goToStep('step-3-distant');
      });
    }

    // Step 3C: Sunglasses Types (Tinted & Course) & Swatches (Up to 40 Colors each)
    const previewSunglassesImg = document.getElementById('la-sunglasses-preview-img');
    const tintedGrid = document.getElementById('la-sunglasses-colors-tinted');
    const courseGrid = document.getElementById('la-sunglasses-colors-course');

    // Sunglasses Type Card Click Event (Toggle between Tinted and Course)
    document.querySelectorAll('.la-type-card[data-la-type-group="sunglasses"]').forEach(card => {
      card.addEventListener('click', () => {
        const radio = card.querySelector('input[type="radio"]');
        if (radio) radio.checked = true;

        document.querySelectorAll('.la-type-card[data-la-type-group="sunglasses"]').forEach(c => {
          c.classList.remove('is-selected');
          const badge = c.querySelector('.la-type-check-badge');
          if (badge) badge.style.display = 'none';
        });

        card.classList.add('is-selected');
        const activeBadge = card.querySelector('.la-type-check-badge');
        if (activeBadge) activeBadge.style.display = 'inline';

        const typeVal = card.getAttribute('data-type') || card.querySelector('input')?.value || 'Tinted';
        this.state.sunglassesType = typeVal;

        if (typeVal === 'Course') {
          if (tintedGrid) tintedGrid.style.display = 'none';
          if (courseGrid) courseGrid.style.display = 'grid';

          const activeCourseCard = courseGrid?.querySelector('.la-color-swatch-card.is-selected') || courseGrid?.querySelector('.la-color-swatch-card');
          if (activeCourseCard) {
            this.state.sunglassesColor = activeCourseCard.querySelector('input')?.value || activeCourseCard.querySelector('.la-color-title')?.textContent?.trim() || 'Gray';
            const courseImg = activeCourseCard.getAttribute('data-img-url');
            if (previewSunglassesImg && courseImg) {
              swapPreviewImage(previewSunglassesImg, courseImg);
            }
          }
        } else {
          // Tinted
          if (courseGrid) courseGrid.style.display = 'none';
          if (tintedGrid) tintedGrid.style.display = 'grid';

          const activeTintedCard = tintedGrid?.querySelector('.la-color-swatch-card.is-selected') || tintedGrid?.querySelector('.la-color-swatch-card');
          if (activeTintedCard) {
            this.state.sunglassesColor = activeTintedCard.querySelector('input')?.value || activeTintedCard.querySelector('.la-color-title')?.textContent?.trim() || 'Gray';
            const tintedImg = activeTintedCard.getAttribute('data-img-url');
            if (previewSunglassesImg && tintedImg) {
              swapPreviewImage(previewSunglassesImg, tintedImg);
            }
          }
        }
      });
    });

    // Sunglasses Color Swatch Card Click Events (Handles both Tinted and Course grids)
    const initSunglassesSwatchGroup = (groupSelector) => {
      document.querySelectorAll(groupSelector).forEach(card => {
        card.addEventListener('click', () => {
          const parentGrid = card.closest('.la-color-grid');
          const radio = card.querySelector('input[type="radio"]');
          if (radio) radio.checked = true;

          if (parentGrid) {
            parentGrid.querySelectorAll('.la-color-swatch-card').forEach(c => {
              c.classList.remove('is-selected');
              const badge = c.querySelector('.la-color-check-badge');
              if (badge) badge.style.display = 'none';
            });
          }

          card.classList.add('is-selected');
          const activeBadge = card.querySelector('.la-color-check-badge');
          if (activeBadge) activeBadge.style.display = 'inline';

          const colorVal = card.querySelector('input')?.value || card.querySelector('.la-color-title')?.textContent?.trim() || 'Gray';
          this.state.sunglassesColor = colorVal;

          const imgUrl = card.getAttribute('data-img-url');
          if (previewSunglassesImg && imgUrl) {
            swapPreviewImage(previewSunglassesImg, imgUrl);
          }
        });
      });
    };

    initSunglassesSwatchGroup('.la-color-swatch-card[data-la-color-group="sunglasses-tinted"]');
    initSunglassesSwatchGroup('.la-color-swatch-card[data-la-color-group="sunglasses-course"]');

    // Step 3C: Sunglasses Save & Continue
    const btnSaveSunglasses = document.getElementById('la-save-sunglasses');
    if (btnSaveSunglasses) {
      btnSaveSunglasses.addEventListener('click', () => {
        this.goToStep('step-3-distant');
      });
    }

    // Step 4: Add your recipe method selection
    document.querySelectorAll('[data-la-recipe-method]').forEach(card => {
      card.addEventListener('click', () => {
        const method = card.getAttribute('data-la-recipe-method');
        this.state.recipeMethod = method;

        if (method === 'Uploaden en extraheren' || method === 'Upload and Extract') {
          this.goToStep('step-5-upload-extract');
        } else if (method === 'Bestand uploaden' || method === 'Upload file') {
          this.goToStep('step-5-upload-file');
        } else if (method === 'Handmatige invoer' || method === 'Manual entry') {
          this.goToStep('step-5-manual-entry');
        } else if (method === 'Later mailen' || method === 'Email later') {
          this.updateSummary();
          this.goToStep('step-6-review');
        }
      });
    });

    // Step 5A: Upload & Extract file input
    const fileExtractInput = document.getElementById('la-file-extract-input');
    if (fileExtractInput) {
      fileExtractInput.addEventListener('change', (e) => {
        if (e.target.files && e.target.files[0]) {
          this.state.recipeFile = e.target.files[0];
          this.updateSummary();
          this.goToStep('step-6-review');
        }
      });
    }

    // Step 5B: Upload File & PD Radio toggles
    const pdRadioTotal = document.getElementById('la-pd-radio-total');
    const pdRadioEye = document.getElementById('la-pd-radio-eye');
    const pdSingleBox = document.getElementById('la-pd-single-box');
    const pdDoubleBox = document.getElementById('la-pd-double-box');

    if (pdRadioTotal && pdRadioEye) {
      pdRadioTotal.addEventListener('change', () => {
        this.state.pdType = 'Totale PD';
        if (pdSingleBox) pdSingleBox.style.display = 'block';
        if (pdDoubleBox) pdDoubleBox.style.display = 'none';
      });
      pdRadioEye.addEventListener('change', () => {
        this.state.pdType = 'PD per oog';
        if (pdSingleBox) pdSingleBox.style.display = 'none';
        if (pdDoubleBox) pdDoubleBox.style.display = 'flex';
      });
    }

    const btnSaveUploadFile = document.getElementById('la-save-upload-file');
    if (btnSaveUploadFile) {
      btnSaveUploadFile.addEventListener('click', () => {
        const fileInput = document.getElementById('la-file-upload-input');
        if (fileInput && fileInput.files && fileInput.files[0]) {
          this.state.recipeFile = fileInput.files[0];
        }

        const totalPdSelect = document.getElementById('la-total-pd-select');
        const rightPdSelect = document.getElementById('la-right-pd-select');
        const linksPdSelect = document.getElementById('la-links-pd-select');

        if (totalPdSelect) this.state.totalPd = totalPdSelect.value;
        if (rightPdSelect) this.state.pdRight = rightPdSelect.value;
        if (linksPdSelect) this.state.pdLinks = linksPdSelect.value;

        this.updateSummary();
        this.goToStep('step-6-review');
      });
    }

    // Step 5C: Manual Entry Radio Toggles & Save
    const manualPdRadioTotal = document.getElementById('la-manual-pd-radio-total');
    const manualPdRadioEye = document.getElementById('la-manual-pd-radio-eye');
    const manualPdSingleBox = document.getElementById('la-manual-pd-single-box');
    const manualPdDoubleBox = document.getElementById('la-manual-pd-double-box');

    if (manualPdRadioTotal && manualPdRadioEye) {
      manualPdRadioTotal.addEventListener('change', () => {
        this.state.pdType = 'Totale PD';
        if (manualPdSingleBox) manualPdSingleBox.style.display = 'block';
        if (manualPdDoubleBox) manualPdDoubleBox.style.display = 'none';
      });
      manualPdRadioEye.addEventListener('change', () => {
        this.state.pdType = 'PD per oog';
        if (manualPdSingleBox) manualPdSingleBox.style.display = 'none';
        if (manualPdDoubleBox) manualPdDoubleBox.style.display = 'flex';
      });
    }

    const btnSaveManual = document.getElementById('la-save-manual');
    if (btnSaveManual) {
      btnSaveManual.addEventListener('click', () => {
        // Collect OD fields
        this.state.manualOD.sph = document.getElementById('la-od-sph')?.value || '0.00';
        this.state.manualOD.cyl = document.getElementById('la-od-cyl')?.value || '0.00';
        this.state.manualOD.ash = document.getElementById('la-od-ash')?.value || 'Selecteer';
        this.state.manualOD.add = document.getElementById('la-od-add')?.value || 'Selecteer';

        // Collect OS fields
        this.state.manualOS.sph = document.getElementById('la-os-sph')?.value || '0.00';
        this.state.manualOS.cyl = document.getElementById('la-os-cyl')?.value || '0.00';
        this.state.manualOS.ash = document.getElementById('la-os-ash')?.value || 'Selecteer';
        this.state.manualOS.add = document.getElementById('la-os-add')?.value || 'Selecteer';

        // Collect PD fields
        const manualTotalPd = document.getElementById('la-manual-total-pd');
        const manualRightPd = document.getElementById('la-manual-right-pd');
        const manualLinksPd = document.getElementById('la-manual-links-pd');

        if (manualTotalPd) this.state.totalPd = manualTotalPd.value;
        if (manualRightPd) this.state.pdRight = manualRightPd.value;
        if (manualLinksPd) this.state.pdLinks = manualLinksPd.value;

        // Segment Height
        this.state.segmentHeight = document.getElementById('la-segment-height')?.value || 'Selecteer';

        // Optional File Upload in Manual Entry
        const manualFileInput = document.getElementById('la-manual-file-input');
        if (manualFileInput && manualFileInput.files && manualFileInput.files[0]) {
          this.state.recipeFile = manualFileInput.files[0];
        }

        this.updateSummary();
        this.goToStep('step-6-review');
      });
    }

    // Step 6: Order Notes & Add to Cart
    const notesInput = document.getElementById('la-order-notes');
    if (notesInput) {
      notesInput.addEventListener('input', (e) => {
        this.state.orderNotes = e.target.value;
      });
    }

    const btnAddToCart = document.getElementById('la-btn-add-to-cart');
    if (btnAddToCart) {
      btnAddToCart.addEventListener('click', () => this.addToCart());
    }
  }

  openModal() {
    this.modal.classList.add('is-active');
    document.body.style.overflow = 'hidden';
  }

  closeModal() {
    this.modal.classList.remove('is-active');
    document.body.style.overflow = '';
  }

  goToStep(stepId) {
    const currentStepEl = document.getElementById(`la-${this.currentStepId}`);
    const nextStepEl = document.getElementById(`la-${stepId}`);

    if (currentStepEl) currentStepEl.classList.remove('is-active');
    if (nextStepEl) nextStepEl.classList.add('is-active');

    this.historyStack.push(stepId);
    this.currentStepId = stepId;

    // Show/hide back button
    const backBtn = document.getElementById('la-btn-back');
    if (backBtn) {
      backBtn.style.visibility = this.historyStack.length > 1 ? 'visible' : 'hidden';
    }

    // Dynamic Preview Image Sync upon entering Step 3B or Step 3C
    if (stepId === 'step-3-matching') {
      const activeCard = document.querySelector('.la-color-swatch-card[data-la-color-group="matching"].is-selected') || document.querySelector('.la-color-swatch-card[data-la-color-group="matching"]');
      const previewImg = document.getElementById('la-matching-preview-img');
      if (activeCard && previewImg) {
        const imgUrl = activeCard.getAttribute('data-img-url');
        if (imgUrl) previewImg.src = imgUrl;
        const colorVal = activeCard.querySelector('input')?.value || activeCard.querySelector('.la-color-title')?.textContent?.trim() || 'Gray';
        this.state.matchingColor = colorVal;
      }
    } else if (stepId === 'step-3-sunglasses') {
      const activeType = this.state.sunglassesType || 'Tinted';
      const grid = activeType === 'Course' ? document.getElementById('la-sunglasses-colors-course') : document.getElementById('la-sunglasses-colors-tinted');
      const activeCard = grid?.querySelector('.la-color-swatch-card.is-selected') || grid?.querySelector('.la-color-swatch-card');
      const previewImg = document.getElementById('la-sunglasses-preview-img');
      if (activeCard && previewImg) {
        const imgUrl = activeCard.getAttribute('data-img-url');
        if (imgUrl) previewImg.src = imgUrl;
        const colorVal = activeCard.querySelector('input')?.value || activeCard.querySelector('.la-color-title')?.textContent?.trim() || 'Gray';
        this.state.sunglassesColor = colorVal;
      }
    }
  }

  goBack() {
    if (this.historyStack.length <= 1) return;

    const currentStepId = this.historyStack.pop();
    const prevStepId = this.historyStack[this.historyStack.length - 1];

    const currentStepEl = document.getElementById(`la-${currentStepId}`);
    const prevStepEl = document.getElementById(`la-${prevStepId}`);

    if (currentStepEl) currentStepEl.classList.remove('is-active');
    if (prevStepEl) prevStepEl.classList.add('is-active');

    this.currentStepId = prevStepId;

    const backBtn = document.getElementById('la-btn-back');
    if (backBtn) {
      backBtn.style.visibility = this.historyStack.length > 1 ? 'visible' : 'hidden';
    }
  }

  updateSummary() {
    const summaryGlassTitle = document.getElementById('la-summary-glass-title');
    const summaryGlassBadge = document.getElementById('la-summary-glass-price-badge');
    const summaryPrescriptionType = document.getElementById('la-summary-prescription-type');
    const summaryMethod = document.getElementById('la-summary-method');
    const summaryLensGroup = document.getElementById('la-summary-lens-group');

    if (summaryGlassTitle) {
      if (this.state.glassOption === 'Meekleuren') {
        summaryGlassTitle.textContent = `Meekleuren (${this.state.matchingColor})`;
      } else if (this.state.glassOption === 'Zonnebril') {
        summaryGlassTitle.textContent = `Zonnebril (${this.state.sunglassesType} - ${this.state.sunglassesColor})`;
      } else {
        summaryGlassTitle.textContent = this.state.glassOption || 'Helder';
      }
    }
    if (summaryGlassBadge) {
      summaryGlassBadge.textContent = this.state.glassPrice > 0 ? `+€${this.state.glassPrice.toFixed(2)}` : 'Inbegrepen';
    }
    if (summaryPrescriptionType) summaryPrescriptionType.textContent = `Prescription type: ${this.state.prescriptionType || 'Multifocaal'}`;
    if (summaryMethod) summaryMethod.textContent = `Methode: ${this.state.recipeMethod || 'Later mailen'}`;
    if (summaryLensGroup) {
      const priceText = this.state.lensPrice > 0 ? ` (+€${this.state.lensPrice.toFixed(2)})` : '';
      summaryLensGroup.textContent = `Glasgroep: ${this.state.lensOption || 'Inbegrepen'}${priceText}`;
    }
  }

  async addToCart() {
    const btn = document.getElementById('la-btn-add-to-cart');
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'TOEVOEGEN...';
    }

    const mainVariantId = window.lensAdvisorConfig?.variantId;
    const addOnVariantId = window.lensAdvisorConfig?.addOnVariantId;

    // Calculate total add-on extra price (Glass option + Lens thinness option)
    const totalExtraPrice = (this.state.glassPrice || 0) + (this.state.lensPrice || 0);

    // Prepare line item properties for MAIN product (Prescription details)
    const properties = {
      'Voorschrifttype': this.state.prescriptionType,
      'Methode': this.state.recipeMethod
    };

    // Only include Glassoptie/Glasdikte on Main product if NO extra fee product is being added
    if (totalExtraPrice === 0) {
      properties['Glasoptie'] = this.state.glassOption || 'Helder';
      properties['Glasdikte'] = this.state.lensOption || 'Inbegrepen';
      if (this.state.glassOption === 'Meekleuren') {
        properties['Kleur'] = this.state.matchingColor;
      }
      if (this.state.glassOption === 'Zonnebril') {
        properties['Zonnebril Type'] = this.state.sunglassesType;
        properties['Zonnebril Kleur'] = this.state.sunglassesColor;
      }
    }

    if (this.state.recipeMethod === 'Handmatige invoer') {
      properties['OD (Rechts)'] = `SPH: ${this.state.manualOD.sph}, CYL: ${this.state.manualOD.cyl}, AS: ${this.state.manualOD.ash}, ADD: ${this.state.manualOD.add}`;
      properties['OS (Links)'] = `SPH: ${this.state.manualOS.sph}, CYL: ${this.state.manualOS.cyl}, AS: ${this.state.manualOS.ash}, ADD: ${this.state.manualOS.add}`;
      if (this.state.pdType === 'Totale PD') {
        properties['Totale PD'] = this.state.totalPd;
      } else {
        properties['PD Rechts'] = this.state.pdRight;
        properties['PD Links'] = this.state.pdLinks;
      }
      properties['Segmenthoogte'] = this.state.segmentHeight;
    } else if (this.state.recipeMethod === 'Bestand uploaden' || this.state.recipeMethod === 'Uploaden en extraheren') {
      if (this.state.pdType === 'Totale PD') {
        properties['Totale PD'] = this.state.totalPd;
      } else {
        properties['PD Rechts'] = this.state.pdRight;
        properties['PD Links'] = this.state.pdLinks;
      }
    }

    if (this.state.orderNotes) {
      properties['Bestelnotities'] = this.state.orderNotes;
    }

    // Use FormData for binary file upload (Shopify CDN link in admin) & cart display order
    const formData = new FormData();
    let itemIndex = 0;

    // 1. Add-on product FIRST (Contains all paid upgrade option details!)
    if (totalExtraPrice > 0 && addOnVariantId) {
      formData.append(`items[${itemIndex}][id]`, addOnVariantId);
      formData.append(`items[${itemIndex}][quantity]`, Math.round(totalExtraPrice));
      formData.append(`items[${itemIndex}][properties][Montuur]`, window.lensAdvisorConfig?.productTitle || 'Montuur');
      
      if (this.state.glassOption) {
        formData.append(`items[${itemIndex}][properties][Glasoptie]`, this.state.glassOption + (this.state.glassPrice > 0 ? ` (+€${this.state.glassPrice})` : ''));
      }
      if (this.state.lensOption) {
        formData.append(`items[${itemIndex}][properties][Glasdikte]`, this.state.lensOption + (this.state.lensPrice > 0 ? ` (+€${this.state.lensPrice})` : ''));
      }
      if (this.state.glassOption === 'Meekleuren') {
        formData.append(`items[${itemIndex}][properties][Kleur]`, this.state.matchingColor);
      }
      if (this.state.glassOption === 'Zonnebril') {
        formData.append(`items[${itemIndex}][properties][Zonnebril Type]`, this.state.sunglassesType);
        formData.append(`items[${itemIndex}][properties][Zonnebril Kleur]`, this.state.sunglassesColor);
      }
      formData.append(`items[${itemIndex}][properties][Omschrijving]`, `Lens & Glas Upgrade Fee (€${totalExtraPrice})`);

      itemIndex++;
    }

    // 2. Main Product SECOND (lands on top of cart display!)
    formData.append(`items[${itemIndex}][id]`, mainVariantId);
    formData.append(`items[${itemIndex}][quantity]`, '1');

    for (const [key, value] of Object.entries(properties)) {
      formData.append(`items[${itemIndex}][properties][${key}]`, value);
    }

    // Append binary file object if uploaded (Shopify will host it on cdn.shopify.com and create a clickable admin order link)
    if (this.state.recipeFile) {
      formData.append(`items[${itemIndex}][properties][Recept Bestand]`, this.state.recipeFile, this.state.recipeFile.name);
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        this.closeModal();
        // Trigger Minimog Cart Drawer update
        if (window.MinimogTheme && window.MinimogTheme.pubsub) {
          window.MinimogTheme.pubsub.publish('PUB_SUB_EVENTS.cartUpdate');
        } else {
          window.location.href = '/cart';
        }
      } else {
        alert('Kon artikelen niet toevoegen aan winkelwagen. Probeer het opnieuw.');
      }
    } catch (err) {
      console.error('Error adding to cart:', err);
      alert('Er is een fout opgetreden. Probeer het opnieuw.');
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.textContent = 'TOEVOEGEN AAN WINKELWAGEN';
      }
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.lensAdvisorInstance = new LensAdvisor();
});
